'use client';
import React, { useEffect, useRef } from 'react';
import { useSession, signIn } from 'next-auth/react';
import type { Session } from 'next-auth';
import { Loader2 } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import AuthPayload from '@/types/authPayloadType';
import { fetchUserWithRetry } from '@/lib/utils/user';
import { fetchOrganizationByUUID } from '@/apis/fetchOrganization';
import { AnimatePresence } from 'framer-motion';
import { SignIn } from '@/components/Auth/SignIn';
import { Suspense } from 'react';
import { mountUrlWithParams } from '@/lib/utils/app-routes';
import { useUserLogged } from '@/hooks/useUserLogged';
import { useUserContext } from '@/contexts/UserContext';
import { UserWithSession } from '@/types/userType';
import { getAuthInfo, saveAuthInfo } from '@/apis/ApiUtil';
import { fetchOnboardingTracker } from '@/apis/onboardingTracker';
import { isLoopbackUrl } from '@/lib/utils/cliAuth';
import { getFirstIntegration } from './helpers';

const ROUTES = {
  HOME: '/home',
  ONBOARDING: '/onboarding',
  SIGNUP: '/signup',
  LOGIN: '/login',
  EXTENSION_LOGIN: '/extension/login',
} as const;

const QUERY_PARAMS = {
  CALLBACK_URL: 'callbackURLAfterSignIn',
  EXTENSION_NAME: 'extensionName',
} as const;

const PROVIDERS = {
  GITHUB: 'github',
  GITLAB: 'gitlab',
} as const;

const STORAGE_KEYS = {
  USER_EMAIL: 'UserEmail',
} as const;

const HEADERS = {
  CONTENT_TYPE: 'Content-Type',
  ACCESS_CONTROL_ALLOW_ORIGIN: 'Access-Control-Allow-Origin',
  ACCESS_CONTROL_ALLOW_METHODS: 'Access-Control-Allow-Methods',
  USER_UUID: 'Useruuid',
  USER_EMAIL: 'Useremail',
  USERNAME: 'Username',
} as const;

type SessionUserWithProvider = Session['user'] & {
  provider: string;
  accessToken?: string;
  refreshToken?: string;
  ghUserName?: string;
  glUserName?: string;
};

const isSessionUserWithProvider = (
  user: Session['user'] | undefined
): user is SessionUserWithProvider =>
  typeof user === 'object' &&
  user !== null &&
  'provider' in user &&
  typeof (user as { provider?: unknown }).provider === 'string';

const buildHeaders = (
  userUUID?: string,
  userEmail?: string
): Record<string, string> => ({
  [HEADERS.CONTENT_TYPE]: 'application/json',
  [HEADERS.ACCESS_CONTROL_ALLOW_ORIGIN]: '*',
  [HEADERS.ACCESS_CONTROL_ALLOW_METHODS]: 'POST,OPTIONS',
  ...(userUUID ? { [HEADERS.USER_UUID]: userUUID } : {}),
  ...(userEmail ? { [HEADERS.USER_EMAIL]: userEmail } : {}),
  [HEADERS.USERNAME]: '',
});

const isSessionExpired = (session: Session | null | undefined): boolean => {
  if (!session?.expires) {
    return false;
  }

  return Date.now() > new Date(session.expires).getTime();
};

const LoginContent = () => {
  const router = useRouter();
  const { data: session, status } = useSession();
  const rawSessionUser = session?.user;
  const sessionUser: SessionUserWithProvider | undefined =
    isSessionUserWithProvider(rawSessionUser) ? rawSessionUser : undefined;
  const sessionEmail = sessionUser?.email ?? null;
  const { user, organization } = useUserLogged();
  const { isUserLoading, isOrgLoading, fetchUser } = useUserContext();
  const searchParams = useSearchParams();
  const extensionName = searchParams?.get('extensionName');
  const callbackURL = searchParams?.get('callbackURLAfterSignIn');
  const isCli = searchParams?.get('cli');
  const cliRedirect = searchParams?.get('cli_redirect');
  const cliState = searchParams?.get('cli_state');
  const hasValidatedRef = useRef(false);
  const hasRedirectedRef = useRef(false);
  const currentUserEmail = user?.Email ?? user?.email ?? null;

  useEffect(() => {
    if (isCli === 'true') {
      localStorage.setItem('cli_signup_flow', 'true');
      // Persist the CLI loopback params so they survive the OAuth round-trip and
      // any onboarding hops before we forward to /cli-auth.
      if (cliRedirect) {
        localStorage.setItem('cli_redirect', cliRedirect);
      }
      if (cliState) {
        localStorage.setItem('cli_state', cliState);
      }
    }
  }, [isCli, cliRedirect, cliState]);

  // Send an authenticated CLI user to their final destination: the loopback
  // authorize page when the CLI handed us a valid `cli_redirect` (auto-capture),
  // otherwise the API settings page for manual copy. Clears the CLI flow flags.
  const redirectCliFlow = () => {
    // Live query params win over the stash. `useSearchParams` is empty on the
    // first client render, so the effect that persists these can land AFTER the
    // redirect decision does, and reading storage alone then loses a handoff
    // that is sitting right there in the URL and drops the user on /settings.
    const storedRedirect = cliRedirect || localStorage.getItem('cli_redirect');
    const storedState = cliState || localStorage.getItem('cli_state') || '';
    localStorage.removeItem('cli_signup_flow');
    localStorage.removeItem('cli_redirect');
    localStorage.removeItem('cli_state');

    if (isLoopbackUrl(storedRedirect)) {
      const qs = new URLSearchParams({
        cli_redirect: storedRedirect as string,
        cli_state: storedState,
      });
      // Client redirect required.
      router.push(`/cli-auth?${qs.toString()}`);
      return;
    }
    // Client redirect required.
    router.push('/settings?tab=api');
  };

  // Clean up obsolete query params (loading, updateScope) - they're no longer used
  // If present, remove them from URL to prevent confusion
  useEffect(() => {
    const hasObsoleteParams =
      searchParams?.has('loading') || searchParams?.has('updateScope');

    if (hasObsoleteParams) {
      // Build clean URL without obsolete params
      const cleanParams = new URLSearchParams();
      if (callbackURL) {
        cleanParams.set('callbackURLAfterSignIn', callbackURL);
      }
      if (extensionName) {
        cleanParams.set('extensionName', extensionName);
      }
      const cleanUrl = cleanParams.toString()
        ? `/login?${cleanParams.toString()}`
        : '/login';
      // Client redirect required.
      router.replace(cleanUrl);
    }
  }, [searchParams, callbackURL, extensionName, router]);

  const redirect = async (redirectUrl: string) => {
    if (!session || !sessionUser?.provider) {
      return;
    }

    if (isSessionExpired(session)) {
      await signIn(sessionUser.provider, { redirect: false });
      return;
    }

    const fetchedUser =
      sessionUser.email || (sessionUser as { id?: string }).id
        ? await fetchUserWithRetry(sessionUser, 2)
        : null;

    const payloadSaveAuthInfo: AuthPayload = {
      AccessToken: sessionUser.accessToken,
      RefreshToken: sessionUser.refreshToken,
      IntegrationName: sessionUser.provider,
    };

    if (sessionUser.provider === PROVIDERS.GITHUB) {
      payloadSaveAuthInfo.GithubUserName = sessionUser.ghUserName;
    } else {
      payloadSaveAuthInfo.GitlabUserName = sessionUser.glUserName;
    }

    if (fetchedUser) {
      payloadSaveAuthInfo.UserUUID = fetchedUser.UserUUID;
      if (sessionUser.email) {
        localStorage.setItem(STORAGE_KEYS.USER_EMAIL, sessionUser.email);
      }
      // Client redirect required.
      router.push(redirectUrl);
    }
  };

  const hasPendingOnboarding = async (
    currentUser: UserWithSession | null | undefined
  ): Promise<boolean> => {
    if (!currentUser?.UserUUID) {
      return false;
    }

    // Wrapped-provisioned orgs already exist but still owe real onboarding.
    // The flag lives on the ORG, so fetch it fresh here (the org store may not
    // be hydrated yet on this call path) rather than trusting cached state.
    // Skip in CLI flow: CLI auth completes normally; they onboard later on web.
    const isCliFlow =
      isCli === 'true' ||
      (typeof window !== 'undefined' &&
        localStorage.getItem('cli_signup_flow') === 'true');
    if (!isCliFlow && currentUser.OrganizationUUID) {
      try {
        const orgRes = await fetchOrganizationByUUID(
          currentUser.OrganizationUUID
        );
        const org = orgRes?.Org ?? orgRes;
        // Completion latch wins over the mid-flow routing switch: a wrapped org
        // whose RequiresOnboarding was never cleared must not drag a user who
        // already finished onboarding back into the flow.
        if (org?.OnboardingCompleted === true) {
          return false;
        }
        if (org?.RequiresOnboarding === true) {
          return true;
        }
      } catch {
        // Non-fatal: fall through to the normal org/tracker decision.
      }
    }

    // Users already attached to an org should not be forced back to onboarding.
    if (currentUser.OrganizationUUID) {
      return false;
    }

    try {
      const trackerRes = await fetchOnboardingTracker({
        UserUUID: currentUser.UserUUID,
        OrgUUID: currentUser.OrganizationUUID || undefined,
        GitHubUsername: currentUser.GithubUsername || undefined,
        GitLabUsername: currentUser.GitlabUsername || undefined,
      });

      if (trackerRes.Error || !trackerRes.Exists) {
        return false;
      }

      const step = (trackerRes.LastCompletedStep || '').trim().toLowerCase();
      return step.length > 0 && step !== 'completed';
    } catch {
      return false;
    }
  };

  const validateUserScope = async (user: UserWithSession) => {
    await checkIntegrationAccess(user);
    // Scopes are now requested upfront in provider config, no need to check/update
    accessToOrganization(user);
  };

  const checkIntegrationAccess = async (user: UserWithSession) => {
    if (isSessionExpired(session) && sessionUser?.provider) {
      await signIn(sessionUser.provider, { redirect: false });
      return null;
    }

    if (!user.accessToken) {
      return null;
    }

    const userEmail = user.Email ?? sessionEmail ?? undefined;
    const hasIntegration = await getAuthInfo(
      user.UserUUID,
      user.provider ?? 'github',
      user.OrganizationUUID,
      buildHeaders(user.UserUUID, userEmail)
    );

    const integration = getFirstIntegration(hasIntegration);
    if (integration) {
      return integration;
    }

    // If no integration, create one with the user Information.
    // RefreshToken must come from the live next-auth session — `User` doesn't
    // carry it, but the backend needs it to refresh expired GitLab access
    // tokens via pr_reviewer/gitlab_oauth.try_refresh_gitlab_integration.
    const payloadSaveAuthInfo: AuthPayload = {
      AccessToken: user.accessToken,
      RefreshToken: session?.user?.refreshToken,
      IntegrationName: user.provider,
    };

    if (user.provider === PROVIDERS.GITHUB) {
      payloadSaveAuthInfo.GithubUserName = user.GithubUsername;
    } else {
      payloadSaveAuthInfo.GitlabUserName = user.GitlabUsername;
    }

    payloadSaveAuthInfo.UserUUID = user.UserUUID ?? '';
    payloadSaveAuthInfo.OrgUUID = user.OrganizationUUID ?? '';

    const response = await saveAuthInfo(payloadSaveAuthInfo);

    if (!response?.Error) {
      return payloadSaveAuthInfo;
    }

    return null;
  };

  const accessToOrganization = async (user: UserWithSession) => {
    // If the user is signing in to accept a workspace invite, never force them
    // into the create-workspace flow — they don't have an org yet because the
    // whole point of the invite is to join one.
    const isJoinWorkspaceCallback =
      !!callbackURL && callbackURL.startsWith('/join-workspace');
    if (isJoinWorkspaceCallback) {
      hasRedirectedRef.current = true;
      router.push(callbackURL);
      return;
    }

    const shouldResumeOnboarding = await hasPendingOnboarding(user);
    if (shouldResumeOnboarding) {
      hasRedirectedRef.current = true;
      // Client redirect required.
      router.push(ROUTES.ONBOARDING);
      return;
    }

    const hasJoinedToOrganization = await checkOrganizationAccess(user);
    if (!hasJoinedToOrganization) {
      redirect(ROUTES.ONBOARDING);
    } else {
      followRedirect();
    }
  };

  const checkOrganizationAccess = (user: UserWithSession): boolean => {
    const userUUID = user?.OrganizationUUID;

    if (!userUUID) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (isSessionExpired(session) && sessionUser?.provider) {
      void signIn(sessionUser.provider, { redirect: false });
      return;
    }

    // Skip validateUserScope for CLI flow - let handleRedirect handle it
    const isCliFlow =
      isCli === 'true' || localStorage.getItem('cli_signup_flow') === 'true';

    if (
      !hasValidatedRef.current &&
      sessionEmail &&
      currentUserEmail &&
      !extensionName &&
      !isCliFlow
    ) {
      hasValidatedRef.current = true;
      validateUserScope(user as UserWithSession);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validateUserScope is redefined every render; hasValidatedRef guards against re-invocation, adding it would just churn deps without changing behavior
  }, [
    sessionEmail,
    currentUserEmail,
    extensionName,
    isCli,
    session,
    sessionUser?.provider,
    user,
  ]);

  useEffect(() => {
    const handleRedirect = async () => {
      // Prevent multiple redirects
      if (hasRedirectedRef.current) {
        return;
      }

      const isCliFlow =
        isCli === 'true' ||
        localStorage.getItem('cli_signup_flow') === 'true';

      if (isSessionExpired(session) && sessionUser?.provider) {
        await signIn(sessionUser.provider, { redirect: false });
        return;
      }

      if (
        sessionEmail &&
        currentUserEmail &&
        user?.UserUUID &&
        !isUserLoading &&
        !isOrgLoading
      ) {
        // Invite accept wins over pending-onboarding / no-org redirects.
        const isJoinWorkspaceCallback =
          !!callbackURL && callbackURL.startsWith('/join-workspace');
        if (isJoinWorkspaceCallback) {
          hasRedirectedRef.current = true;
          router.push(callbackURL);
          return;
        }

        const shouldResumeOnboarding = extensionName
          ? false
          : await hasPendingOnboarding(user as UserWithSession);

        if (shouldResumeOnboarding) {
          hasRedirectedRef.current = true;
          // Client redirect required.
          router.push(ROUTES.ONBOARDING);
          return;
        }

        if (isCliFlow && (organization?.OrgUUID || user?.OrganizationUUID)) {
          hasRedirectedRef.current = true;
          redirectCliFlow();
          return;
        }

        hasRedirectedRef.current = true;
        if (extensionName) {
          // Client redirect required.
          router.push(
            `${ROUTES.EXTENSION_LOGIN}?${QUERY_PARAMS.EXTENSION_NAME}=${extensionName}`
          );
        } else if (!organization?.OrgUUID && !user?.OrganizationUUID) {
          // Client redirect required.
          router.push(ROUTES.ONBOARDING);
        } else if (callbackURL && !callbackURL.includes('/login')) {
          // Client redirect required.
          router.push(callbackURL);
        } else {
          // Client redirect required.
          router.push(ROUTES.HOME);
        }
      } else if (
        sessionEmail &&
        (!currentUserEmail || !user?.UserUUID) &&
        !isUserLoading
      ) {
        try {
          if (sessionUser) {
            await fetchUser(sessionUser);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };

    handleRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- redirectCliFlow is redefined every render; hasRedirectedRef guards against duplicate redirects, adding it would just churn deps without changing behavior
  }, [
    sessionEmail,
    session,
    sessionUser?.provider,
    currentUserEmail,
    user?.UserUUID,
    extensionName,
    isCli,
    callbackURL,
    router,
    organization?.OrgUUID,
    isUserLoading,
    isOrgLoading,
    fetchUser,
    sessionUser,
    user,
  ]);

  const followRedirect = () => {
    // Prevent multiple redirects
    if (hasRedirectedRef.current) {
      return;
    }

    // Check for CLI flow first
    const isCliFlow =
      isCli === 'true' || localStorage.getItem('cli_signup_flow') === 'true';
    if (isCliFlow && (organization?.OrgUUID || user?.OrganizationUUID)) {
      hasRedirectedRef.current = true;
      redirectCliFlow();
      return;
    }

    hasRedirectedRef.current = true;
    // Prevent redirect loops by checking if callbackURL is the login page
    if (callbackURL && !callbackURL.includes('/login')) {
      redirect(callbackURL);
    } else if (searchParams?.get('extensionName')) {
      const extensionNameParam = searchParams?.get('extensionName');
      redirect(
        mountUrlWithParams(ROUTES.EXTENSION_LOGIN, {
          [QUERY_PARAMS.EXTENSION_NAME]: extensionNameParam || '',
        })
      );
    } else {
      redirect(ROUTES.HOME);
    }
  };

  if (
    status === 'loading' ||
    (sessionEmail && (isUserLoading || isOrgLoading || !user?.UserUUID))
  ) {
    return (
      <div className="flex w-full h-screen items-center justify-center bg-ds-brand-darker">
        <Loader2
          className="h-8 w-8 animate-spin text-ds-text-subtle"
          aria-hidden
        />
      </div>
    );
  }

  return (
    <div className="h-screen bg-ds-brand-darker p-[20px] flex items-stretch">
      <AnimatePresence mode="wait">
        <SignIn key="signin" callbackUrl={callbackURL || undefined} />
      </AnimatePresence>
    </div>
  );
};

const LoginPage = () => {
  return (
    <Suspense
      fallback={
        <div className="flex justify-center items-center h-full">
          <Loader2
            className="h-8 w-8 animate-spin text-ds-text-subtle"
            aria-hidden
          />
        </div>
      }
    >
      <LoginContent />
    </Suspense>
  );
};

export default LoginPage;
